import UploadFile from '@/components/UploadFile'
import UploadWebsiteUrls from '@/components/UploadWebsiteUrl'
import ProfileEdit from '@/components/ProfileEdit'
import { RegionData } from '@/models/Regions'
import { IwiData } from '@/models/Iwi'
import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { setPostLoginRedirect } from '@/utils/postLoginRedirect'
import { isOrgCreationDisabled } from '@/utils/utils'
import { useCurrentUserOrgs, useOrgDocs } from '@/utils/hooks/orgs'
import OrgUserAdd from '@/components/OrgUserAdd'
import OrgUsersInvitationsList from '@/components/OrgUsersInvitationsList'
import OrgUsersList from '@/components/OrgUsersList'
import { useInvitations, useOrgUsers } from '@/utils/hooks/invitations'
import { useRegions, useRegionSets } from '@/utils/hooks/regions'
import { LocationsServedSummary } from '@/components/LocationSelector'
import { useIwi } from '@/utils/hooks/iwi'
import { OrgData } from '@/models/Orgs'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Alert,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CachedIcon from '@mui/icons-material/Cached'
import RefetchConfirmDialog from '@/components/RefetchConfirmDialog'
import { isRootUrl } from '@/utils/utils'
import {
  AccessibleInfoIcon,
  AccessibleTooltip,
} from '../components/AccessibleTooltip'
import { UUID } from 'crypto'
import { useEffect, useState, useMemo } from 'react'
import {
  useNavigate,
  useParams,
  useLocation,
  useBlocker,
} from 'react-router-dom'
import GenerateEditProfile from '@/components/GenerateEditProfile'
import LoginSignup from '@/components/LoginSignup'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import OnboardingLockBanner from '@/components/OnboardingLockBanner'
import usePermissions from '@/utils/hooks/users'
import AccountPage from '@/pages/AccountPage'
import OrgMatchPreferences from '@/components/OrgMatchPreferences'
import AccountSecuritySettings from '@/components/AccountSecuritySettings'
import NotificationsTab from '@/components/NotificationsTab/NotificationsTab'
import { localisedDate } from '@/utils/dateUtils'
import { useUnsavedChanges } from '@/utils/hooks/useUnsavedChanges'
import { useStagedContentGuard } from '@/components/StagedContentGuardProvider'
import { storedDocDisplayName } from '@/utils/uploadDedup'

const MY_ACCOUNT_PATH = '/my-account'
const MY_ACCOUNT_TAB = 'my-account'
const NOTIFICATIONS_PATH = '/notifications'
const NOTIFICATIONS_TAB = 'notifications'

const BASE_PROFILE_TABS = [
  'details',
  'locations',
  'documents',
  'funding-profile',
] as const
const PREFERENCES_PROFILE_TABS = ['match-preferences'] as const
const ADMIN_PROFILE_TABS = ['accounts', 'users'] as const
const NOTIFICATIONS_PROFILE_TABS = [NOTIFICATIONS_TAB] as const
const ACCOUNT_PROFILE_TABS = [MY_ACCOUNT_TAB] as const
const TAB_LABELS: Record<string, string> = {
  details: 'Organisation Details',
  locations: 'Locations',
  documents: 'Supporting Documents',
  'funding-profile': 'Funding Profile',
  'match-preferences': 'Match Preferences',
  accounts: 'Accounts',
  users: 'Users',
  notifications: 'Notifications',
  [MY_ACCOUNT_TAB]: 'Login & Security',
}
type ProfileTabKey =
  | (typeof BASE_PROFILE_TABS)[number]
  | (typeof PREFERENCES_PROFILE_TABS)[number]
  | (typeof ADMIN_PROFILE_TABS)[number]
  | (typeof NOTIFICATIONS_PROFILE_TABS)[number]
  | (typeof ACCOUNT_PROFILE_TABS)[number]

// Create a TabPanel component
interface TabPanelProps {
  children?: React.ReactNode
  index: string
  value: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: string) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`,
  }
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isMyAccountRoute = location.pathname === MY_ACCOUNT_PATH
  const isNotificationsRoute = location.pathname === NOTIFICATIONS_PATH
  const pageTitle = isMyAccountRoute
    ? 'Manage Login'
    : isNotificationsRoute
      ? 'Notifications'
      : 'Organisation Profile'
  const { tab } = useParams()
  usePageTitle(pageTitle)
  const { canWrite: canWriteSubscription } = usePausedOrg()
  const { isOnboarding } = useOnboardingStatus()
  const canWrite = canWriteSubscription && !isOnboarding
  const { permissions, isLoading: permissionsLoading } = usePermissions()
  const canManageUsers = permissions?.role === 'admin'
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  // Shown after a successful delete: removing a document does not strip its
  // information from an already-generated profile, so point the user to editing.
  const [showRemovalNote, setShowRemovalNote] = useState(false)
  const [refetchingId, setRefetchingId] = useState<number | null>(null)
  const [refetchError, setRefetchError] = useState<string | null>(null)
  const [refetchDialogOpen, setRefetchDialogOpen] = useState(false)
  const [pendingRefetchId, setPendingRefetchId] = useState<number | null>(null)
  const [saved, setSaved] = useState<boolean>(false)
  const [showUploadType, setShowUploadType] = useState<string>('files')
  const [uploadTabValue, setUploadTabValue] = useState(0)

  // Shared unsaved-changes registry for the settings area (staged files, staged
  // websites, profile-text edits). Drives the single nav blocker + the sub-tab
  // switch guard, and is the source of truth.
  const { hasUnsavedChanges, isSectionUnsaved, reportUnsaved, clearAll } =
    useUnsavedChanges()

  // One blocker for the whole settings area. Main tabs are route changes, so this
  // catches tab switches and any external navigation. Children report their unsaved state up.
  useBlocker(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Are you sure you want to leave this page? You have unsaved changes.',
      )
      if (confirmed) {
        clearAll()
        return false
      }
      return true
    }
    return false
  })

  // Warn on full page unload (refresh, tab close) too.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handler = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedChanges])

  // The org selector lives outside this page, in the shared nav - it can't
  // see hasUnsavedChanges directly, so report it up for the org-switch guard.
  const { reportStagedContent } = useStagedContentGuard()
  useEffect(() => {
    reportStagedContent('profile', hasUnsavedChanges)
    return () => reportStagedContent('profile', false)
  }, [hasUnsavedChanges, reportStagedContent])
  const [error, setError] = useState<boolean>(false)
  const focusRef = useFocusManagement()

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const {
    orgData,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])
  const [organisation, setOrganisation] = useState<OrgData | undefined>(
    orgData?.[0],
  ) // Added OrgData type

  const navigateToTab = (tabKey: string) => {
    if (tabKey === MY_ACCOUNT_TAB) {
      navigate(MY_ACCOUNT_PATH)
    } else if (tabKey === NOTIFICATIONS_TAB) {
      navigate(NOTIFICATIONS_PATH)
    } else {
      navigate(`/profile/${tabKey}`)
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    navigateToTab(newValue)
  }

  // A user can land here with zero orgs if they were removed from their
  // only org while their org list was still cached (the route-level gate in
  // AuthenticatedRoute.tsx checks that same cached list, so it doesn't catch
  // this). This used to silently create a brand-new empty org for them
  // instead - send them to onboarding the same way AuthenticatedRoute
  // already does for every other no-org case, rather than quietly handing a
  // just-removed person an orphan org (#1541).
  useEffect(() => {
    if (
      !userLoading &&
      !orgsLoading &&
      user &&
      token &&
      orgData?.length === 0 &&
      !isOrgCreationDisabled()
    ) {
      navigate('/onboarding', { replace: true })
    }
  }, [user, token, orgData, userLoading, orgsLoading, navigate])

  useEffect(() => {
    if (user && orgData && orgData.length > 0) {
      if (user.selected_org_uuid) {
        const selectedOrg = orgData.find(
          (org: OrgData) => org.uuid === user.selected_org_uuid,
        )
        if (selectedOrg) {
          setOrganisation(selectedOrg)
          return
        }
      }
      // If no selected_org_uuid or selected org not found, set to the first org
      if (orgData[0]) {
        setOrganisation(orgData[0])
      }
    }
  }, [user, orgData])

  const canManagePreferences = organisation?.can_set_preferences ?? false

  const allowedTabKeys = useMemo(
    () =>
      [
        ...BASE_PROFILE_TABS,
        ...(canManagePreferences ? PREFERENCES_PROFILE_TABS : []),
        ...(canManageUsers ? ADMIN_PROFILE_TABS : []),
        ...NOTIFICATIONS_PROFILE_TABS,
        ...ACCOUNT_PROFILE_TABS,
      ] as ProfileTabKey[],
    [canManageUsers, canManagePreferences],
  )

  const isAllowedTab = (value: string): value is ProfileTabKey =>
    allowedTabKeys.includes(value as ProfileTabKey)

  const requestedTab = isMyAccountRoute
    ? MY_ACCOUNT_TAB
    : isNotificationsRoute
      ? NOTIFICATIONS_TAB
      : (tab ?? 'details')
  const activeTab: ProfileTabKey = isAllowedTab(requestedTab)
    ? requestedTab
    : 'details'

  useEffect(() => {
    if (
      permissionsLoading ||
      orgsLoading ||
      isMyAccountRoute ||
      isNotificationsRoute
    ) {
      return
    }

    if (!tab || !isAllowedTab(tab)) {
      // A logged-out visitor hitting an admin-only tab (e.g.
      // /profile/accounts from an email CTA) reads as non-admin and gets
      // bounced to details. Remember where they were headed first, so they
      // return there after logging in and gaining admin access — mirrors
      // how the invitation flow preserves its destination.
      const isAuthed = !!(user && token)
      if (
        !isAuthed &&
        tab &&
        (ADMIN_PROFILE_TABS as readonly string[]).includes(tab)
      ) {
        setPostLoginRedirect(`/profile/${tab}`)
      }
      navigate('/profile/details', { replace: true })
    }
  }, [
    tab,
    allowedTabKeys,
    navigate,
    user,
    token,
    permissionsLoading,
    orgsLoading,
    isMyAccountRoute,
    isNotificationsRoute,
  ])

  // Conditionally call useOrgDocs only when organisation.uuid is available
  const {
    data: docs,
    isLoading: docsLoading,
    uploadOrgDocs,
    deleteOrgDoc,
    uploadOrgWebsites,
    refetchOrgWebsite,
    updateOrgWebsite,
    isError: docsError,
  } = useOrgDocs(organisation?.uuid as UUID, token as string)

  const {
    regions,
    isLoading: regionsLoading,
    isError: regionsError,
  } = useRegions(token as string)

  const { regionSets } = useRegionSets(token as string)

  const {
    iwi: iwiList,
    isLoading: iwiLoading,
    isError: iwiError,
  } = useIwi(token as string)

  const orgUuid = organisation?.uuid ?? ''
  const { data: orgUsers, isLoading: orgUsersLoading } = useOrgUsers(
    canManageUsers ? token : null,
    canManageUsers ? orgUuid : '',
  )

  const { data: invitations, isLoading: invitationsLoading } = useInvitations(
    canManageUsers ? token : null,
    canManageUsers ? orgUuid : '',
  )

  // lookup map for iwi
  const iwiMap = useMemo(() => {
    if (!Array.isArray(iwiList)) return new Map()
    return new Map(iwiList.map((iwi: IwiData) => [iwi.id, iwi]))
  }, [iwiList])

  const filteredOrgUsers =
    orgUsers?.filter((orgUser) => orgUser.uuid !== user?.uuid) ?? []

  const triggerRefetch = async (docId: number, fullCrawl: boolean) => {
    setRefetchError(null)
    setRefetchingId(docId)
    try {
      await refetchOrgWebsite.mutateAsync({
        token: token as string,
        docId,
        fullCrawl,
      })
    } catch {
      setRefetchError(
        "Sorry, we couldn't get the latest content. Please try again.",
      )
    } finally {
      setRefetchingId(null)
    }
  }

  const handleRefetchClick = (docId: number, websiteUrl: string) => {
    if (isRootUrl(websiteUrl)) {
      setPendingRefetchId(docId)
      setRefetchDialogOpen(true)
    } else {
      triggerRefetch(docId, false)
    }
  }

  const handleSinglePageRefetch = () => {
    setRefetchDialogOpen(false)
    if (pendingRefetchId !== null) triggerRefetch(pendingRefetchId, false)
  }

  const handleFullCrawlRefetch = async (saveAsOrgWebsite: boolean) => {
    setRefetchDialogOpen(false)
    if (saveAsOrgWebsite && organisation?.uuid) {
      const pendingDoc = docs?.find((d) => d.id === pendingRefetchId)
      if (pendingDoc?.website_url) {
        try {
          await updateOrgWebsite.mutateAsync({
            token: token as string,
            orgUuid: organisation.uuid,
            website: pendingDoc.website_url,
          })
        } catch {
          // Non-fatal
        }
      }
    }
    if (pendingRefetchId !== null) triggerRefetch(pendingRefetchId, true)
  }

  const handleDeleteDoc = async (docId: number) => {
    setDeleteError(null)
    setShowRemovalNote(false)

    // Show confirmation dialog
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this? This action cannot be undone.',
    )

    if (!confirmDelete) {
      return
    }

    setDeletingId(docId)

    try {
      await deleteOrgDoc.mutateAsync({
        token: token as string,
        docId: docId,
      })
      // Only nudge toward editing if a profile already exists to be inaccurate.
      if (organisation?.profile_text) {
        setShowRemovalNote(true)
      }
    } catch (error) {
      setDeleteError('An error occurred while deleting the document')
    } finally {
      setDeletingId(null)
    }
  }

  function handleUploadTabChange(
    event: React.SyntheticEvent,
    newValue: number,
  ) {
    // Sub-tab switch is local state (not a route)
    // this will guard the section we're leaving against unsaved staged content here.
    const leavingKey = showUploadType === 'files' ? 'files' : 'website'
    if (isSectionUnsaved(leavingKey)) {
      const confirmed = window.confirm(
        'Are you sure you want to leave this page? You have unsaved changes that will be discarded.',
      )
      if (!confirmed) return
      reportUnsaved(leavingKey, false)
    }
    setUploadTabValue(newValue)
    setShowUploadType(newValue === 0 ? 'files' : 'websites')
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [editing, setEditing] = useState(false)
  const [invitingUser, setInvitingUser] = useState(false)

  // Redirect if org creation is disabled and user has no orgs
  if (
    !userLoading &&
    !orgsLoading &&
    orgData?.length === 0 &&
    isOrgCreationDisabled()
  ) {
    window.location.href = '/'
    return null // Prevent further rendering if redirecting
  }

  // Show loading if user, orgs, docs, regions or iwi are loading, or if
  // we're about to redirect to onboarding (no orgs) - avoids flashing this
  // page's own content for the one render before that effect fires.
  if (
    userLoading ||
    orgsLoading ||
    docsLoading ||
    regionsLoading ||
    iwiLoading ||
    (orgData?.length === 0 && !isOrgCreationDisabled())
  ) {
    return <Loading />
  }

  // Show error message if there's an error fetching initial data
  if (isError || docsError || regionsError || iwiError) {
    return <ErrorMessage />
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          <RefetchConfirmDialog
            open={refetchDialogOpen}
            onClose={() => setRefetchDialogOpen(false)}
            onSinglePage={handleSinglePageRefetch}
            onFullCrawl={handleFullCrawlRefetch}
            isLoading={refetchingId !== null}
            showSaveAsWebsite={!organisation?.website}
          />
          {!user ||
            !token ||
            ((!organisation || !organisation.uuid) && <ErrorMessage />)}
          {organisation ? (
            <Box
              sx={{ width: '100%', px: isMobile ? 1 : 4, mx: 'auto', mt: 2 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  ml: isMobile ? 0 : 11,
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant={isMobile ? 'h4' : 'h1'}
                  component="h1"
                  ref={focusRef}
                  tabIndex={-1}
                  sx={{
                    mb: 1,
                    textAlign: 'left',
                    fontSize: isMobile ? '28px' : '34px',
                    '&:focus': {
                      outline: 'none',
                    },
                  }}
                >
                  {pageTitle}
                </Typography>
              </Box>
              {/* )} */}
              <Box
                sx={{
                  p: isMobile ? 1 : 3,
                  px: isMobile ? 1 : 6,
                  mb: 4,
                  pb: isMobile ? 12 : 6,
                  boxShadow: 'none',
                }}
              >
                {isMobile ? (
                  <Box sx={{ mb: 2 }}>
                    <Select
                      value={activeTab}
                      onChange={(e) => navigateToTab(e.target.value as string)}
                      fullWidth
                      sx={{
                        '& .MuiSelect-select': {
                          py: 1.5,
                          fontSize: '16px',
                        },
                      }}
                    >
                      {allowedTabKeys.map((key) => (
                        <MenuItem key={key} value={key}>
                          {TAB_LABELS[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                ) : (
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      aria-label="profile tabs"
                    >
                      <Tab
                        value="details"
                        disableRipple
                        label="Organisation Details"
                        {...a11yProps('details')}
                      />
                      <Tab
                        value="locations"
                        disableRipple
                        label="Locations"
                        {...a11yProps('locations')}
                      />
                      <Tab
                        value="documents"
                        disableRipple
                        label="Supporting Documents"
                        {...a11yProps('documents')}
                      />
                      <Tab
                        value="funding-profile"
                        disableRipple
                        label="Funding Profile"
                        {...a11yProps('funding-profile')}
                      />
                      {canManagePreferences && (
                        <Tab
                          value="match-preferences"
                          disableRipple
                          label="Match Preferences"
                          {...a11yProps('match-preferences')}
                        />
                      )}
                      {canManageUsers && (
                        <Tab
                          value="accounts"
                          disableRipple
                          label="Accounts"
                          {...a11yProps('accounts')}
                        />
                      )}
                      {canManageUsers && (
                        <Tab
                          value="users"
                          disableRipple
                          label="Users"
                          {...a11yProps('users')}
                        />
                      )}
                      <Tab
                        value="notifications"
                        disableRipple
                        label="Notifications"
                        {...a11yProps('notifications')}
                      />
                      <Tab
                        value={MY_ACCOUNT_TAB}
                        disableRipple
                        label={TAB_LABELS[MY_ACCOUNT_TAB]}
                        {...a11yProps(MY_ACCOUNT_TAB)}
                      />
                    </Tabs>
                  </Box>
                )}

                {/* Organisation Details Tab */}
                <TabPanel value={activeTab} index="details">
                  {isOnboarding && <OnboardingLockBanner />}
                  {organisation && editing ? (
                    <Box
                      key={`edit-${organisation.uuid}`}
                      sx={{
                        margin: '0 auto',
                        marginTop: 2,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <ProfileEdit
                        organisation={organisation}
                        token={token as string}
                        section="details"
                        setSaved={(savedState) => {
                          setSaved(savedState)
                          // If profile was saved successfully, close the edit form immediately
                          if (savedState) {
                            setEditing(false)
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      key={organisation.uuid}
                      sx={{
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        pl: isMobile ? 0 : 2,
                      }}
                    >
                      <Box
                        sx={{
                          mt: 1,
                          pr: 1,
                          mb: isMobile ? 1 : -5,
                          display: 'flex',
                          justifyContent: 'flex-end',
                          width: '100%',
                          zIndex: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          disableRipple
                          disabled={!canWrite}
                          sx={{
                            color: '#1C3045',
                            textTransform: 'none',
                            borderColor: '#1C3045',
                            border: 2,
                            borderRadius: 2,
                            fontSize: '16px',
                            px: 8,
                            '&:focus': {
                              backgroundColor: '#e8e8e8',
                            },
                          }}
                          onClick={() => setEditing(!editing)}
                        >
                          Edit
                        </Button>
                      </Box>
                      <List>
                        <ListItem>
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            mr={2}
                            minWidth={'150px'}
                          >
                            Organisation Name:
                          </Typography>

                          {organisation.name}
                        </ListItem>
                        <ListItem>
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            mr={2}
                            minWidth={'150px'}
                          >
                            Charities Number:{' '}
                          </Typography>{' '}
                          {organisation.charities_number || 'None'}
                        </ListItem>

                        <ListItem
                          sx={{ display: 'flex', alignItems: 'flex-start' }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            mr={2}
                            sx={{ minWidth: '150px', flexShrink: 0 }}
                          >
                            Locations Served:
                          </Typography>
                          <Typography variant="body1" sx={{ flexGrow: 1 }}>
                            {organisation?.region_ids &&
                            organisation.region_ids.length > 0 &&
                            (regions?.length ?? 0) > 0 ? (
                              <LocationsServedSummary
                                regions={regions}
                                regionSets={regionSets}
                                selectedIds={organisation.region_ids}
                              />
                            ) : (
                              'No Location Specified'
                            )}
                          </Typography>
                        </ListItem>

                        {(!organisation?.region_ids ||
                          organisation.region_ids.length === 0) && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              ml: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                bgcolor: '#fff3cd',
                                color: '#856404',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                fontSize: '14px',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                ⚠️ Specifying location(s) will improve the
                                quality of matches
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {organisation?.iwi && organisation.iwi.length > 0 && (
                          <ListItem>
                            <Typography
                              variant="body1"
                              fontWeight={'500'}
                              mr={2}
                              minWidth={'150px'}
                            >
                              Iwi:
                            </Typography>{' '}
                            <Typography variant="body1" sx={{ flexGrow: 1 }}>
                              {(iwiList?.length ?? 0) > 0
                                ? organisation.iwi
                                    .map(
                                      (id: number) =>
                                        iwiMap.get(id)?.name || 'Unknown Iwi',
                                    )
                                    .join(', ')
                                : 'Loading...'}
                            </Typography>
                          </ListItem>
                        )}
                        <ListItem>
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            minWidth={'150px'}
                          >
                            Accepts Gambling Proceeds:{' '}
                          </Typography>
                          <AccessibleInfoIcon
                            tooltip="Proceeds from Lotteries, Pokies, and Casinos"
                            sx={{
                              fontSize: '16px',
                              color: 'gray',
                              ml: 0.5,
                              mt: -1.5,
                              mr: 2,
                            }}
                            size="small"
                          />

                          {organisation.accepts_gambling_proceeds
                            ? 'Yes'
                            : 'No'}
                        </ListItem>
                        <ListItem>
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            minWidth={'150px'}
                          >
                            Accepts International Funding:{' '}
                          </Typography>
                          <AccessibleInfoIcon
                            tooltip="Funding Opportunities from Outside of New Zealand"
                            sx={{
                              fontSize: '16px',
                              color: 'gray',
                              ml: 0.5,
                              mt: -1.5,
                              mr: 2,
                            }}
                            size="small"
                          />

                          {organisation.accepts_international ? 'Yes' : 'No'}
                        </ListItem>

                        <ListItem sx={{ alignItems: 'flex-start' }}>
                          <Typography
                            variant="body1"
                            fontWeight={'500'}
                            mr={2}
                            width="200px"
                          >
                            Description:
                          </Typography>
                        </ListItem>
                        <ListItem sx={{ alignItems: 'flex-start' }}>
                          <Box
                            tabIndex={0}
                            aria-label="Organisation Description"
                            sx={{
                              flex: 1,
                              p: 2,
                              border: '1px solid rgba(0, 0, 0, 0.12)',
                              borderRadius: 1,
                              maxHeight: '400px',
                              overflowY: 'auto',
                            }}
                          >
                            {organisation.description ? (
                              <Typography
                                variant="body1"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {organisation.description}
                              </Typography>
                            ) : (
                              <Typography variant="body1">
                                No description
                              </Typography>
                            )}
                          </Box>
                        </ListItem>
                      </List>
                    </Box>
                  )}
                </TabPanel>

                {/* Locations Tab */}
                <TabPanel value={activeTab} index="locations">
                  {isOnboarding && <OnboardingLockBanner />}
                  {organisation && (
                    <Box
                      key={`locations-${organisation.uuid}`}
                      sx={{ margin: '0 auto', marginTop: 2, width: '100%' }}
                    >
                      <ProfileEdit
                        organisation={organisation}
                        token={token as string}
                        section="locations"
                        setSaved={(savedState) => setSaved(savedState)}
                      />
                    </Box>
                  )}
                </TabPanel>

                {/* Supporting Documents Tab */}
                <TabPanel value={activeTab} index="documents">
                  {isOnboarding && <OnboardingLockBanner />}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Document and Website Upload Section */}

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body1" paragraph sx={{ pl: 2 }}>
                        Upload files or provide website links to help us better
                        understand your organisation and improve profile
                        generation.
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: '#f4f9ff',
                          border: '1px solid #cce5ff',
                          borderRadius: 1,
                          p: 1,
                          minWidth: '250px',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: '600', mb: 0.5 }}
                        >
                          Useful examples include:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          <Typography component="li" variant="body2">
                            Past funding applications
                          </Typography>
                          <Typography component="li" variant="body2">
                            Strategic plans
                          </Typography>
                          <Typography component="li" variant="body2">
                            Annual reports
                          </Typography>
                          <Typography component="li" variant="body2">
                            News articles
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Upload Controls */}
                    <Box
                      sx={{
                        width: '100%',
                        mb: 3,
                        pl: 2,
                      }}
                    >
                      <Tabs
                        value={uploadTabValue}
                        onChange={handleUploadTabChange}
                        sx={{
                          '& .MuiTabs-indicator': {
                            display: 'none',
                          },
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '16px',
                            minHeight: '48px',
                            borderRadius: '4px',
                            border: '1px solid #1C3045',
                            marginRight: '16px',
                            minWidth: '120px',
                            '&.Mui-selected': {
                              backgroundColor: '#1C3045',
                              color: '#fff',
                            },
                            '&:not(.Mui-selected)': {
                              backgroundColor: '#fff',
                              color: '#1C3045',
                            },
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: '#1C3045',
                            },
                            '&:focus': {
                              border: '2px solid #1C3045',
                            },
                          },
                        }}
                      >
                        <Tab disableRipple label="Upload Files" />
                        <Tab disableRipple label="Upload Websites" />
                      </Tabs>
                    </Box>

                    {/* Upload Components */}
                    <Box sx={{ mb: 4, pl: 2 }}>
                      {showUploadType === 'files' && (
                        <UploadFile
                          orgUuid={organisation.uuid}
                          token={token as string}
                          uploadOrgDocs={uploadOrgDocs}
                          disabled={isOnboarding}
                          onUnsavedChange={(v) => reportUnsaved('files', v)}
                          existingDocNames={
                            docs
                              ?.filter((d) => !d.website_url)
                              .map((d) => storedDocDisplayName(d.doc_url)) ?? []
                          }
                        />
                      )}
                      {showUploadType === 'websites' && (
                        <UploadWebsiteUrls
                          orgUuid={organisation.uuid}
                          token={token as string}
                          uploadOrgWebsites={uploadOrgWebsites}
                          orgWebsite={organisation?.website}
                          disabled={isOnboarding}
                          onUnsavedChange={(v) => reportUnsaved('website', v)}
                          existingWebsiteUrls={
                            docs
                              ?.filter((d) => d.website_url)
                              .map((d) => d.website_url as string) ?? []
                          }
                        />
                      )}
                    </Box>

                    {/* Uploaded Files List */}
                    <Box sx={{ mt: 3, pl: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        fontWeight="600"
                      >
                        Uploaded Documents
                      </Typography>

                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: '600' }}
                      >
                        Website URLs
                      </Typography>
                      {docs &&
                      docs.filter((file) => file.website_url).length > 0 ? (
                        <List>
                          {docs
                            .filter((file) => file.website_url !== null)
                            .map((file) => (
                              <ListItem
                                key={file.id}
                                secondaryAction={
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <AccessibleTooltip title="Refresh content for this webpage">
                                      <IconButton
                                        disabled={
                                          refetchingId != null ||
                                          deletingId != null ||
                                          !canWrite
                                        }
                                        onClick={() =>
                                          handleRefetchClick(
                                            file.id,
                                            file.website_url as string,
                                          )
                                        }
                                      >
                                        {refetchingId === file.id ? (
                                          <CircularProgress
                                            size={20}
                                            color="inherit"
                                          />
                                        ) : (
                                          <CachedIcon />
                                        )}
                                      </IconButton>
                                    </AccessibleTooltip>
                                    <IconButton
                                      edge="end"
                                      disabled={
                                        deletingId != null ||
                                        refetchingId != null ||
                                        !canWrite
                                      }
                                      onClick={() => handleDeleteDoc(file.id)}
                                      sx={{ color: '#d32f2f' }}
                                      aria-label={`Delete uploaded website URL: ${file.website_url}`}
                                    >
                                      {deletingId === file.id ? (
                                        <CircularProgress
                                          size={20}
                                          color="inherit"
                                        />
                                      ) : (
                                        <DeleteOutlineIcon />
                                      )}
                                    </IconButton>
                                  </Box>
                                }
                              >
                                <Box>
                                  <Link
                                    href={file.website_url as string}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {file.website_url}
                                  </Link>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    Date Added:{' '}
                                    {localisedDate(
                                      new Date(file.date_added),
                                      'dd MMMM yyyy',
                                    )}
                                  </Typography>
                                  {deletingId === file.id && (
                                    <Typography
                                      variant="caption"
                                      color="#d32f2f"
                                      display="block"
                                    >
                                      Deleting...
                                    </Typography>
                                  )}
                                  {refetchingId === file.id && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Updating...
                                    </Typography>
                                  )}
                                </Box>
                              </ListItem>
                            ))}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
                          No websites uploaded
                        </Typography>
                      )}

                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 2, mb: 1, fontWeight: '600' }}
                      >
                        Files
                      </Typography>
                      {docs &&
                      docs.filter((file) => !file.website_url).length > 0 ? (
                        <List>
                          {docs
                            .filter((file) => !file.website_url)
                            .map((file) => {
                              const fileName = file.doc_url
                                .replace(/^.*?(\d{8}_\d{6}_)/, '')
                                .replace(/_/g, ' ')
                              return (
                                <ListItem
                                  key={file.id}
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      disabled={deletingId != null || !canWrite}
                                      onClick={() => handleDeleteDoc(file.id)}
                                      sx={{ color: '#d32f2f' }}
                                      aria-label={`Delete uploaded file: ${fileName}`}
                                    >
                                      {deletingId === file.id ? (
                                        <CircularProgress
                                          size={20}
                                          color="inherit"
                                        />
                                      ) : (
                                        <DeleteOutlineIcon />
                                      )}
                                    </IconButton>
                                  }
                                >
                                  <Box>
                                    <ListItemText primary={fileName} />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Date Added:{' '}
                                      {localisedDate(
                                        new Date(file.date_added),
                                        'dd MMMM yyyy',
                                      )}
                                    </Typography>
                                    {deletingId === file.id && (
                                      <Typography
                                        variant="caption"
                                        color="#d32f2f"
                                        ml={2}
                                      >
                                        Deleting file...
                                      </Typography>
                                    )}
                                  </Box>
                                </ListItem>
                              )
                            })}
                        </List>
                      ) : (
                        <Typography variant="body2" sx={{ ml: 2, mb: 2 }}>
                          No files uploaded
                        </Typography>
                      )}

                      {deleteError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {deleteError}
                        </Alert>
                      )}
                      {showRemovalNote && (
                        <Alert
                          severity="info"
                          sx={{ mt: 2 }}
                          onClose={() => setShowRemovalNote(false)}
                        >
                          Document removed. If this document&apos;s information
                          is no longer accurate, edit your funding profile to
                          remove it — deleting the document doesn&apos;t change
                          a profile you&apos;ve already generated.
                        </Alert>
                      )}
                      {refetchError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {refetchError}
                        </Alert>
                      )}
                    </Box>
                  </Box>
                </TabPanel>

                {/* Funding Profile Tab */}
                <TabPanel value={activeTab} index="funding-profile">
                  {isOnboarding && <OnboardingLockBanner />}
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                  >
                    {/* Profile Generator Section */}
                    <Box>
                      <GenerateEditProfile
                        organisation={organisation}
                        token={token as string}
                        disabled={isOnboarding}
                        onUnsavedChange={(v) => reportUnsaved('profileText', v)}
                      />
                    </Box>
                  </Box>
                </TabPanel>

                {canManagePreferences && (
                  <>
                    {/* Match Preferences Tab */}
                    <TabPanel value={activeTab} index="match-preferences">
                      <OrgMatchPreferences
                        orgUuid={organisation.uuid}
                        token={token as string}
                      />
                    </TabPanel>
                  </>
                )}

                {canManageUsers && (
                  <>
                    {/* Accounts Tab */}
                    <TabPanel value={activeTab} index="accounts">
                      <AccountPage embedded />
                    </TabPanel>

                    {/* Users Tab */}
                    <TabPanel value={activeTab} index="users">
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        {permissionsLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 2,
                            }}
                          >
                            <CircularProgress
                              size={20}
                              sx={{ color: '#1C3045' }}
                            />
                            <Typography variant="body1">
                              Loading permissions...
                            </Typography>
                          </Box>
                        ) : orgUsersLoading || invitationsLoading ? (
                          <Loading />
                        ) : (
                          <>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontSize: '24px',
                                }}
                              >
                                Organisation Users
                              </Typography>
                              <Button
                                variant="contained"
                                disableRipple
                                sx={{
                                  borderRadius: 2,
                                  bgcolor: '#1C3045',
                                  color: 'white',
                                  px: 4,
                                  fontSize: '16px',
                                  textTransform: 'none',
                                  boxShadow: 'none',
                                }}
                                onClick={() => setInvitingUser(true)}
                              >
                                Invite a User
                              </Button>
                            </Box>

                            {invitingUser && organisation && user && token && (
                              <OrgUserAdd
                                invitingUser={invitingUser}
                                setInvitingUser={setInvitingUser}
                                currentUser={user}
                                token={token}
                                orgUuid={organisation.uuid}
                              />
                            )}

                            {token && filteredOrgUsers.length > 0 ? (
                              <OrgUsersList
                                orgUsers={filteredOrgUsers}
                                orgUuid={organisation.uuid}
                                token={token}
                                isMobile={isMobile}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  mt: 4,
                                  backgroundColor: '#f5f5f5',
                                  width: '50%',
                                  mx: 'auto',
                                  height: '20vh',
                                  borderRadius: 3,
                                  border: 1,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  component="h2"
                                  sx={{ textAlign: 'center', mb: 1 }}
                                >
                                  There are no other users in your organisation
                                </Typography>
                                <Typography
                                  variant="body1"
                                  component="h2"
                                  sx={{ textAlign: 'center', mb: 1 }}
                                >
                                  Invite a user to join your organisation with
                                  the button above
                                </Typography>
                              </Box>
                            )}

                            {invitations && invitations.length > 0 && (
                              <>
                                <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                                  Pending Invitations
                                </Typography>
                                <OrgUsersInvitationsList
                                  invitations={invitations}
                                  token={token}
                                  orgUuid={organisation.uuid}
                                  isMobile={isMobile}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    </TabPanel>
                  </>
                )}

                <TabPanel value={activeTab} index="notifications">
                  <NotificationsTab
                    orgUuid={organisation.uuid}
                    orgName={organisation.name}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={MY_ACCOUNT_TAB}>
                  <AccountSecuritySettings />
                </TabPanel>
              </Box>
            </Box>
          ) : null}
        </>
      </Authenticated>
    </>
  )
}
